import { useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  EditableToken,
  RichTextContent,
  EditableSlotGroup,
  useEditMode,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';

/**
 * FeeSheet — Engrain fee catalog. Token prefix feesheet.*
 */

const NA = '—';
function isNA(value: string | null | undefined): boolean {
  if (value == null) return true;
  const v = value.trim();
  return v === '' || v === NA;
}

function shouldPrefixDollar(value: string | null | undefined): boolean {
  if (isNA(value)) return false;
  const v = value!.trim();
  if (v.startsWith('$') || v.includes('%')) return false;
  return /^[\d.,]+$/.test(v);
}

type FeeType = 'Required' | 'Optional' | 'Situational';
type BadgeVariant = 'required' | 'optional' | 'situational';

function FeeBadge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  return (
    <span className={cn('fs-badge', `fs-badge--${variant}`)}>{children}</span>
  );
}

type FeeFields = {
  name: string | null;
  amount: string | null;
  additional: string | null;
  frequency: string | null;
  cost_detail: string | null;
};

function FeeCells({
  fee,
  feeType,
  badge,
}: {
  fee: FeeFields;
  feeType: FeeType;
  badge: BadgeVariant;
}) {
  const amount = fee.amount ?? '';
  const additional = fee.additional ?? '';
  const detail = fee.cost_detail ?? '';
  const hasDetail = !isNA(detail);
  const hasAdditional = !isNA(additional);

  return (
    <>
      <div className="fs-cell" data-label="Fee Name">{fee.name}</div>
      <div className="fs-cell" data-label="Cost">
        <span className="fs-cost-wrap">
          <span
            className={cn(
              'fs-cost',
              shouldPrefixDollar(amount) && 'fs-cost--dollar',
              hasDetail && 'fs-cost--tooltip',
            )}
          >
            {isNA(amount) ? NA : amount}
          </span>
          {hasAdditional && (
            <span className="fs-additional">
              {shouldPrefixDollar(additional) ? `+ $${additional.trim()} additional` : `+ ${additional.trim()} additional`}
            </span>
          )}
          {hasDetail && (
            <span role="tooltip" className="fs-tooltip">{detail}</span>
          )}
        </span>
      </div>
      <div className="fs-cell" data-label="Frequency">{isNA(fee.frequency) ? NA : fee.frequency}</div>
      <div className="fs-cell" data-label="Fee Type">
        <FeeBadge variant={badge}>{feeType}</FeeBadge>
      </div>
    </>
  );
}

function FeePanel({
  title,
  badgeLabel,
  badgeVariant,
  description,
  open,
  onToggle,
  children,
  hasRows,
}: {
  title: string;
  badgeLabel: string;
  badgeVariant: BadgeVariant;
  description: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  hasRows: boolean;
}) {
  return (
    <div className={cn('fs-panel', open && 'fs-panel--open')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="fs-panel-trigger"
      >
        <span className="fs-panel-title">
          {title}
          <FeeBadge variant={badgeVariant}>{badgeLabel}</FeeBadge>
        </span>
        <span className="fs-panel-icon" aria-hidden="true" />
      </button>

      <div className="fs-panel-body" data-open={open} aria-hidden={!open}>
        <div className="fs-panel-body-inner">
          {description}
          {hasRows ? (
            <div className="fs-table">
              <div className="fs-head" aria-hidden="true">
                <div>Fee Name</div>
                <div>Cost</div>
                <div>Frequency</div>
                <div>Fee Type</div>
              </div>
              {children}
            </div>
          ) : (
            <p className="fs-empty">No fees in this category apply at this community.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const applicationDef: SlotGroupDefinition = {
  label: 'Application Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Application Fee' },
    amount: { label: 'Cost', type: 'text', defaultField: 'property_application_fee', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: 'Per applicant.', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const adminDef: SlotGroupDefinition = {
  label: 'Admin Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Admin Fee' },
    amount: { label: 'Cost', type: 'text', defaultField: 'property_admin_fee', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const reservationDef: SlotGroupDefinition = {
  label: 'Reservation Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Reservation Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const securityDef: SlotGroupDefinition = {
  label: 'Security Deposit',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Security Deposit' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const amenityDef: SlotGroupDefinition = {
  label: 'Amenity Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Amenity Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const amenityRenewalDef: SlotGroupDefinition = {
  label: 'Amenity Renewal',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Amenity Renewal' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Annual' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const occupancyDef: SlotGroupDefinition = {
  label: 'Occupancy Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Occupancy Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const certificateDef: SlotGroupDefinition = {
  label: 'Certificate of Occupancy',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Certificate of Occupancy' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const electricDef: SlotGroupDefinition = {
  label: 'Utility — Electric',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Utility — Electric' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const gasDef: SlotGroupDefinition = {
  label: 'Utility — Gas',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Utility — Gas' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const waterDef: SlotGroupDefinition = {
  label: 'Utility — Water & Sewer',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Utility — Water & Sewer' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const utilityAdminDef: SlotGroupDefinition = {
  label: 'Utility Admin / Billing',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Utility Admin / Billing' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const utilitySetupDef: SlotGroupDefinition = {
  label: 'Utility New Account',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Utility New Account' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const trashDef: SlotGroupDefinition = {
  label: 'Trash',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Trash' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const pestDef: SlotGroupDefinition = {
  label: 'Pest Control',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Pest Control' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customRequiredDef: SlotGroupDefinition = {
  label: 'Custom Required Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Required Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const petFeeDef: SlotGroupDefinition = {
  label: 'Pet Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Pet Fee' },
    amount: { label: 'Cost', type: 'text', defaultField: 'property_upfront_pet_fee', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const petRentDef: SlotGroupDefinition = {
  label: 'Pet Rent',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Pet Rent' },
    amount: { label: 'Cost', type: 'text', defaultField: 'property_monthly_pet_rent', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const petFee2ndDef: SlotGroupDefinition = {
  label: 'Pet Fee — 2nd Pet',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Pet Fee — 2nd Pet' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const petRent2ndDef: SlotGroupDefinition = {
  label: 'Pet Rent — 2nd Pet',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Pet Rent — 2nd Pet' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingReservedDef: SlotGroupDefinition = {
  label: 'Parking — Reserved',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Reserved' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingCoveredDef: SlotGroupDefinition = {
  label: 'Parking — Covered',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Covered' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingGarageDef: SlotGroupDefinition = {
  label: 'Parking — Garage',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Garage' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingUnreservedDef: SlotGroupDefinition = {
  label: 'Parking — Unreserved',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Unreserved' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingCustomDef: SlotGroupDefinition = {
  label: 'Parking — Other',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Other' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const parkingCustom2Def: SlotGroupDefinition = {
  label: 'Parking — Other (2)',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Parking — Other (2)' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const storageDef: SlotGroupDefinition = {
  label: 'Storage',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Storage' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const washerDef: SlotGroupDefinition = {
  label: 'Washer/Dryer',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Washer/Dryer' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customOptionalDef: SlotGroupDefinition = {
  label: 'Custom Optional Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Optional Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customOptional2Def: SlotGroupDefinition = {
  label: 'Custom Optional Fee (2)',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Optional Fee (2)' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customOptional3Def: SlotGroupDefinition = {
  label: 'Custom Optional Fee (3)',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Optional Fee (3)' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customOptional4Def: SlotGroupDefinition = {
  label: 'Custom Optional Fee (4)',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Optional Fee (4)' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const monthToMonthDef: SlotGroupDefinition = {
  label: 'Month To Month',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Month To Month' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Monthly' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const transferDef: SlotGroupDefinition = {
  label: 'Transfer Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Transfer Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const buyoutDef: SlotGroupDefinition = {
  label: 'Buy-Out Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Buy-Out Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'One-Time' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const lateDef: SlotGroupDefinition = {
  label: 'Late Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Late Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Per Occurrence' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const nsfDef: SlotGroupDefinition = {
  label: 'NSF Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'NSF Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Per Occurrence' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const lockoutDef: SlotGroupDefinition = {
  label: 'Lockout Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Lockout Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Per Occurrence' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

const customSituationalDef: SlotGroupDefinition = {
  label: 'Custom Situational Fee',
  fields: {
    name: { label: 'Fee Name', type: 'text', defaultValue: 'Custom Situational Fee' },
    amount: { label: 'Cost', type: 'text', defaultValue: '—' },
    additional: { label: 'Additional Amount', type: 'text', defaultValue: '', description: 'Second-step amount on the same row, e.g. extra pet.' },
    frequency: { label: 'Frequency', type: 'text', defaultValue: 'Per Occurrence' },
    cost_detail: { label: 'Cost Detail', type: 'text', defaultValue: '', description: 'Hover tooltip on the cost. Leave empty for no tooltip.' },
  },
};

interface FeeSheetProps {
  showHeader?: boolean;
  wide?: boolean;
}

export function FeeSheet({ showHeader = true, wide = false }: FeeSheetProps) {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const heading = useContentToken('feesheet.heading');
  const application = useSlotGroup('feesheet.application', applicationDef);
  const admin = useSlotGroup('feesheet.admin', adminDef);
  const reservation = useSlotGroup('feesheet.reservation', reservationDef);
  const security = useSlotGroup('feesheet.security', securityDef);
  const amenity = useSlotGroup('feesheet.amenity', amenityDef);
  const amenityRenewal = useSlotGroup('feesheet.amenity_renewal', amenityRenewalDef);
  const occupancy = useSlotGroup('feesheet.occupancy', occupancyDef);
  const certificate = useSlotGroup('feesheet.certificate', certificateDef);
  const electric = useSlotGroup('feesheet.electric', electricDef);
  const gas = useSlotGroup('feesheet.gas', gasDef);
  const water = useSlotGroup('feesheet.water', waterDef);
  const utilityAdmin = useSlotGroup('feesheet.utility_admin', utilityAdminDef);
  const utilitySetup = useSlotGroup('feesheet.utility_setup', utilitySetupDef);
  const trash = useSlotGroup('feesheet.trash', trashDef);
  const pest = useSlotGroup('feesheet.pest', pestDef);
  const customRequired = useSlotGroup('feesheet.custom_required', customRequiredDef);
  const petFee = useSlotGroup('feesheet.pet_fee', petFeeDef);
  const petRent = useSlotGroup('feesheet.pet_rent', petRentDef);
  const petFee2nd = useSlotGroup('feesheet.pet_fee_2nd', petFee2ndDef);
  const petRent2nd = useSlotGroup('feesheet.pet_rent_2nd', petRent2ndDef);
  const parkingReserved = useSlotGroup('feesheet.parking_reserved', parkingReservedDef);
  const parkingCovered = useSlotGroup('feesheet.parking_covered', parkingCoveredDef);
  const parkingGarage = useSlotGroup('feesheet.parking_garage', parkingGarageDef);
  const parkingUnreserved = useSlotGroup('feesheet.parking_unreserved', parkingUnreservedDef);
  const parkingCustom = useSlotGroup('feesheet.parking_custom', parkingCustomDef);
  const parkingCustom2 = useSlotGroup('feesheet.parking_custom_2', parkingCustom2Def);
  const storage = useSlotGroup('feesheet.storage', storageDef);
  const washer = useSlotGroup('feesheet.washer', washerDef);
  const customOptional = useSlotGroup('feesheet.custom_optional', customOptionalDef);
  const customOptional2 = useSlotGroup('feesheet.custom_optional_2', customOptional2Def);
  const customOptional3 = useSlotGroup('feesheet.custom_optional_3', customOptional3Def);
  const customOptional4 = useSlotGroup('feesheet.custom_optional_4', customOptional4Def);
  const monthToMonth = useSlotGroup('feesheet.month_to_month', monthToMonthDef);
  const transfer = useSlotGroup('feesheet.transfer', transferDef);
  const buyout = useSlotGroup('feesheet.buyout', buyoutDef);
  const late = useSlotGroup('feesheet.late', lateDef);
  const nsf = useSlotGroup('feesheet.nsf', nsfDef);
  const lockout = useSlotGroup('feesheet.lockout', lockoutDef);
  const customSituational = useSlotGroup('feesheet.custom_situational', customSituationalDef);

  const showAll = isEditMode;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    required: true,
  });
  const toggle = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const showRequired = showAll || !isNA(application.amount) || !isNA(admin.amount) || !isNA(reservation.amount) || !isNA(security.amount) || !isNA(amenity.amount) || !isNA(amenityRenewal.amount) || !isNA(occupancy.amount) || !isNA(certificate.amount) || !isNA(electric.amount) || !isNA(gas.amount) || !isNA(water.amount) || !isNA(utilityAdmin.amount) || !isNA(utilitySetup.amount) || !isNA(trash.amount) || !isNA(pest.amount) || !isNA(customRequired.amount);
  const showOptional = showAll || !isNA(petFee.amount) || !isNA(petRent.amount) || !isNA(petFee2nd.amount) || !isNA(petRent2nd.amount) || !isNA(parkingReserved.amount) || !isNA(parkingCovered.amount) || !isNA(parkingGarage.amount) || !isNA(parkingUnreserved.amount) || !isNA(parkingCustom.amount) || !isNA(parkingCustom2.amount) || !isNA(storage.amount) || !isNA(washer.amount) || !isNA(customOptional.amount) || !isNA(customOptional2.amount) || !isNA(customOptional3.amount) || !isNA(customOptional4.amount);
  const showSituational = showAll || !isNA(monthToMonth.amount) || !isNA(transfer.amount) || !isNA(buyout.amount) || !isNA(late.amount) || !isNA(nsf.amount) || !isNA(lockout.amount) || !isNA(customSituational.amount);
  const anyVisible = showRequired || showOptional || showSituational;

  return (
    <section className="fs-root">
      <div className={cn('fs-inner', wide && 'fs-inner--wide')}>
        {showHeader && (
          <>
            <EditableToken
              path="feesheet.heading"
              as="h2"
              className="fs-heading"
            >
              {heading}
            </EditableToken>
            <RichTextContent
              path="feesheet.intro"
              as="div"
              defaultValue="<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>"
              className="fs-blurb"
            />
          </>
        )}

        <div className="fs-accordion">
          {showRequired && (
            <FeePanel
              title="Figures to Help You Figure it Out"
              badgeLabel="Required"
              badgeVariant="required"
              description={
                <RichTextContent
                  path="feesheet.required_intro"
                  as="div"
                  defaultValue="<p>Essential fees and utilities to help you understand your base costs and requirements.</p>"
                  className="fs-panel-desc"
                />
              }
              open={isEditMode || !!openSections.required}
              onToggle={() => toggle('required')}
              hasRows={showRequired}
            >
          {(showAll || !isNA(application.amount)) && (
            <EditableSlotGroup groupPath="feesheet.application" definition={applicationDef} className="fs-row">
              <FeeCells
                fee={application}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(admin.amount)) && (
            <EditableSlotGroup groupPath="feesheet.admin" definition={adminDef} className="fs-row">
              <FeeCells
                fee={admin}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(reservation.amount)) && (
            <EditableSlotGroup groupPath="feesheet.reservation" definition={reservationDef} className="fs-row">
              <FeeCells
                fee={reservation}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(security.amount)) && (
            <EditableSlotGroup groupPath="feesheet.security" definition={securityDef} className="fs-row">
              <FeeCells
                fee={security}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(amenity.amount)) && (
            <EditableSlotGroup groupPath="feesheet.amenity" definition={amenityDef} className="fs-row">
              <FeeCells
                fee={amenity}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(amenityRenewal.amount)) && (
            <EditableSlotGroup groupPath="feesheet.amenity_renewal" definition={amenityRenewalDef} className="fs-row">
              <FeeCells
                fee={amenityRenewal}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(occupancy.amount)) && (
            <EditableSlotGroup groupPath="feesheet.occupancy" definition={occupancyDef} className="fs-row">
              <FeeCells
                fee={occupancy}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(certificate.amount)) && (
            <EditableSlotGroup groupPath="feesheet.certificate" definition={certificateDef} className="fs-row">
              <FeeCells
                fee={certificate}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(electric.amount)) && (
            <EditableSlotGroup groupPath="feesheet.electric" definition={electricDef} className="fs-row">
              <FeeCells
                fee={electric}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(gas.amount)) && (
            <EditableSlotGroup groupPath="feesheet.gas" definition={gasDef} className="fs-row">
              <FeeCells
                fee={gas}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(water.amount)) && (
            <EditableSlotGroup groupPath="feesheet.water" definition={waterDef} className="fs-row">
              <FeeCells
                fee={water}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(utilityAdmin.amount)) && (
            <EditableSlotGroup groupPath="feesheet.utility_admin" definition={utilityAdminDef} className="fs-row">
              <FeeCells
                fee={utilityAdmin}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(utilitySetup.amount)) && (
            <EditableSlotGroup groupPath="feesheet.utility_setup" definition={utilitySetupDef} className="fs-row">
              <FeeCells
                fee={utilitySetup}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(trash.amount)) && (
            <EditableSlotGroup groupPath="feesheet.trash" definition={trashDef} className="fs-row">
              <FeeCells
                fee={trash}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(pest.amount)) && (
            <EditableSlotGroup groupPath="feesheet.pest" definition={pestDef} className="fs-row">
              <FeeCells
                fee={pest}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customRequired.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_required" definition={customRequiredDef} className="fs-row">
              <FeeCells
                fee={customRequired}
                feeType="Required"
                badge="required"
              />
            </EditableSlotGroup>
          )}
            </FeePanel>
          )}
          {showOptional && (
            <FeePanel
              title="Extras that Matter"
              badgeLabel="Optional"
              badgeVariant="optional"
              description={
                <RichTextContent
                  path="feesheet.optional_intro"
                  as="div"
                  defaultValue="<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>"
                  className="fs-panel-desc"
                />
              }
              open={isEditMode || !!openSections.optional}
              onToggle={() => toggle('optional')}
              hasRows={showOptional}
            >
          {(showAll || !isNA(petFee.amount)) && (
            <EditableSlotGroup groupPath="feesheet.pet_fee" definition={petFeeDef} className="fs-row">
              <FeeCells
                fee={petFee}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(petRent.amount)) && (
            <EditableSlotGroup groupPath="feesheet.pet_rent" definition={petRentDef} className="fs-row">
              <FeeCells
                fee={petRent}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(petFee2nd.amount)) && (
            <EditableSlotGroup groupPath="feesheet.pet_fee_2nd" definition={petFee2ndDef} className="fs-row">
              <FeeCells
                fee={petFee2nd}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(petRent2nd.amount)) && (
            <EditableSlotGroup groupPath="feesheet.pet_rent_2nd" definition={petRent2ndDef} className="fs-row">
              <FeeCells
                fee={petRent2nd}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingReserved.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_reserved" definition={parkingReservedDef} className="fs-row">
              <FeeCells
                fee={parkingReserved}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingCovered.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_covered" definition={parkingCoveredDef} className="fs-row">
              <FeeCells
                fee={parkingCovered}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingGarage.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_garage" definition={parkingGarageDef} className="fs-row">
              <FeeCells
                fee={parkingGarage}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingUnreserved.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_unreserved" definition={parkingUnreservedDef} className="fs-row">
              <FeeCells
                fee={parkingUnreserved}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingCustom.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_custom" definition={parkingCustomDef} className="fs-row">
              <FeeCells
                fee={parkingCustom}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(parkingCustom2.amount)) && (
            <EditableSlotGroup groupPath="feesheet.parking_custom_2" definition={parkingCustom2Def} className="fs-row">
              <FeeCells
                fee={parkingCustom2}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(storage.amount)) && (
            <EditableSlotGroup groupPath="feesheet.storage" definition={storageDef} className="fs-row">
              <FeeCells
                fee={storage}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(washer.amount)) && (
            <EditableSlotGroup groupPath="feesheet.washer" definition={washerDef} className="fs-row">
              <FeeCells
                fee={washer}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customOptional.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_optional" definition={customOptionalDef} className="fs-row">
              <FeeCells
                fee={customOptional}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customOptional2.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_optional_2" definition={customOptional2Def} className="fs-row">
              <FeeCells
                fee={customOptional2}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customOptional3.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_optional_3" definition={customOptional3Def} className="fs-row">
              <FeeCells
                fee={customOptional3}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customOptional4.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_optional_4" definition={customOptional4Def} className="fs-row">
              <FeeCells
                fee={customOptional4}
                feeType="Optional"
                badge="optional"
              />
            </EditableSlotGroup>
          )}
            </FeePanel>
          )}
          {showSituational && (
            <FeePanel
              title="Situational Fees"
              badgeLabel="Optional"
              badgeVariant="situational"
              description={
                <RichTextContent
                  path="feesheet.situational_intro"
                  as="div"
                  defaultValue="<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>"
                  className="fs-panel-desc"
                />
              }
              open={isEditMode || !!openSections.situational}
              onToggle={() => toggle('situational')}
              hasRows={showSituational}
            >
          {(showAll || !isNA(monthToMonth.amount)) && (
            <EditableSlotGroup groupPath="feesheet.month_to_month" definition={monthToMonthDef} className="fs-row">
              <FeeCells
                fee={monthToMonth}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(transfer.amount)) && (
            <EditableSlotGroup groupPath="feesheet.transfer" definition={transferDef} className="fs-row">
              <FeeCells
                fee={transfer}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(buyout.amount)) && (
            <EditableSlotGroup groupPath="feesheet.buyout" definition={buyoutDef} className="fs-row">
              <FeeCells
                fee={buyout}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(late.amount)) && (
            <EditableSlotGroup groupPath="feesheet.late" definition={lateDef} className="fs-row">
              <FeeCells
                fee={late}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(nsf.amount)) && (
            <EditableSlotGroup groupPath="feesheet.nsf" definition={nsfDef} className="fs-row">
              <FeeCells
                fee={nsf}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(lockout.amount)) && (
            <EditableSlotGroup groupPath="feesheet.lockout" definition={lockoutDef} className="fs-row">
              <FeeCells
                fee={lockout}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
          {(showAll || !isNA(customSituational.amount)) && (
            <EditableSlotGroup groupPath="feesheet.custom_situational" definition={customSituationalDef} className="fs-row">
              <FeeCells
                fee={customSituational}
                feeType="Situational"
                badge="situational"
              />
            </EditableSlotGroup>
          )}
            </FeePanel>
          )}
          {!anyVisible && (
            <p className="fs-none">
              No additional fees apply at this community. Please contact the
              leasing office for full details.
            </p>
          )}
        </div>

        <RichTextContent
          path="feesheet.disclaimer"
          as="div"
          defaultValue="<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>"
          className="fs-disclaimer"
        />
      </div>

      <style>{`
        .fs-root {
          background: color-mix(in srgb, var(--vibe-color-text) 4%, var(--vibe-color-background));
          padding: 3.5rem 0 4.5rem;
        }
        @media (min-width: 768px) {
          .fs-root { padding: 4.5rem 0 5.5rem; }
        }

        .fs-inner {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .fs-inner--wide {
          max-width: 90rem;
        }
        @media (min-width: 640px) {
          .fs-inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .fs-inner { padding: 0 2rem; }
          .fs-inner--wide { padding: 0 2.5rem; }
        }
        @media (min-width: 1400px) {
          .fs-inner--wide {
            max-width: 100rem;
            padding: 0 3rem;
          }
        }

        .fs-heading {
          margin: 0 0 0.75rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-weight: 400;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
          line-height: 1.2;
        }

        .fs-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin-bottom: 1.5rem;
          max-width: 48rem;
        }
        .fs-blurb p { margin: 0 0 0.75em; }
        .fs-blurb p:last-child { margin-bottom: 0; }
        .fs-blurb a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .fs-accordion {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .fs-panel {
          background: #ffffff;
          border-radius: 12px;
          overflow: hidden;
        }
        .fs-panel--open {
          overflow: visible;
        }

        .fs-panel-trigger {
          all: unset;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.5rem;
          cursor: pointer;
          background: #ffffff;
          text-align: left;
          transition: background-color 200ms ease;
        }
        .fs-panel-trigger:hover {
          background: color-mix(in srgb, var(--vibe-color-text) 2%, #ffffff);
        }
        .fs-panel-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .fs-panel-title {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.75rem 0.95rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.15rem, 2vw, 1.5rem);
          font-weight: 400;
          line-height: 1.3;
          color: var(--vibe-color-text);
        }

        .fs-badge {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.03em;
          padding: 0.25rem 0.5rem;
          border-radius: 7px;
          vertical-align: middle;
          white-space: nowrap;
        }
        .fs-badge--required {
          background: #000000;
          color: #ffffff;
        }
        .fs-badge--optional,
        .fs-badge--situational {
          background: #e2e2e2;
          color: #000000;
        }

        .fs-panel-icon {
          position: relative;
          flex: 0 0 auto;
          width: 1.75rem;
          height: 1.75rem;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .fs-panel-icon::after {
          content: '+';
          font-family: var(--vibe-font-body);
          font-size: 1.75rem;
          font-weight: 300;
          line-height: 1;
          color: #4d5259;
          position: relative;
          top: -0.1em;
          transition: transform 250ms ease;
        }
        .fs-panel--open .fs-panel-icon::after {
          content: '−';
          top: 0;
        }

        .fs-panel-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 350ms ease;
          background: #ffffff;
        }
        .fs-panel-body[data-open='true'] {
          grid-template-rows: 1fr;
        }
        .fs-panel-body-inner {
          overflow: hidden;
          padding: 0 1.5rem;
        }
        .fs-panel-body[data-open='true'] .fs-panel-body-inner {
          padding-bottom: 1.5rem;
          overflow: visible;
        }

        .fs-panel-desc {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.45;
          font-weight: 400;
          color: #686d74;
          margin-bottom: 1.5rem;
        }
        .fs-panel-desc p { margin: 0 0 0.5em; }
        .fs-panel-desc p:last-child { margin-bottom: 0; }

        .fs-table {
          width: 100%;
        }
        .fs-head,
        .fs-row {
          display: grid;
          grid-template-columns: 40% 22% 22% 16%;
          align-items: center;
        }
        .fs-head {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.4;
          color: #000000;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e0e0e0;
        }
        .fs-head > div:nth-child(4) {
          text-align: right;
        }
        .fs-row {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          font-weight: 400;
          color: #4d5259;
          padding: 0.95rem 0;
          border-bottom: 1px solid #e0e0e0;
        }
        .fs-row:last-child {
          border-bottom: none;
        }
        .fs-cell:nth-child(4) {
          text-align: right;
        }
        .fs-cost-wrap {
          position: relative;
          display: inline-flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.15rem;
        }
        .fs-cost {
          font-variant-numeric: tabular-nums;
        }
        .fs-cost--tooltip {
          border-bottom: 2px dotted currentColor;
          cursor: help;
        }
        .fs-cost--dollar::before {
          content: '$';
        }
        .fs-additional {
          font-size: 0.8125rem;
          color: #686d74;
        }
        .fs-tooltip {
          display: none;
          position: absolute;
          left: 0;
          bottom: calc(100% + 0.4rem);
          z-index: 5;
          width: max-content;
          min-width: 16rem;
          max-width: 22rem;
          padding: 0.5rem 0.7rem;
          border-radius: 6px;
          background: #1a1a1a;
          color: #ffffff;
          font-size: 0.8125rem;
          line-height: 1.4;
          white-space: normal;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
        }
        .fs-cost-wrap:hover .fs-tooltip,
        .fs-cost-wrap:focus-within .fs-tooltip {
          display: block;
        }

        .fs-empty {
          margin: 0;
          padding: 0.5rem 0 0.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .fs-none {
          margin: 0;
          padding: 1.5rem;
          border-radius: 12px;
          background: #ffffff;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-style: italic;
          color: var(--vibe-color-text-muted);
        }

        .fs-disclaimer {
          margin-top: 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .fs-disclaimer p { margin: 0 0 0.5em; }
        .fs-disclaimer p:last-child { margin-bottom: 0; }
        .fs-disclaimer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        @media (max-width: 1200px) {
          .fs-panel-title {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 767px) {
          .fs-panel-trigger {
            padding: 1.125rem;
            align-items: flex-start;
          }
          .fs-panel-title { font-size: 1.25rem; width: 80%; }
          .fs-panel-body-inner { padding-left: 1.125rem; padding-right: 1.125rem; }
          .fs-panel-body[data-open='true'] .fs-panel-body-inner {
            padding-bottom: 1.125rem;
          }
          .fs-head { display: none; }
          .fs-row {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0.3rem;
            padding: 0.85rem 0;
          }
          .fs-cell {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            text-align: right;
            font-size: 0.9375rem;
          }
          .fs-cell::before {
            content: attr(data-label);
            flex: 0 1 auto;
            font-weight: 600;
            color: #2c3e50;
            text-align: left;
          }
          .fs-cell:first-child {
            justify-content: flex-start;
            text-align: left;
            font-weight: 500;
            color: var(--vibe-color-text);
            padding-bottom: 0.45rem;
          }
          .fs-cell:first-child::before {
            display: none;
          }
          .fs-cell:nth-child(4) {
            text-align: right;
          }
          .fs-disclaimer { text-align: center; }
        }

        @media (prefers-reduced-motion: reduce) {
          .fs-panel-body,
          .fs-panel-icon::after {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeSheet',
  displayName: 'Fee Sheet',
  tokens: {
    'feesheet.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Fee Transparency',
    },
    'feesheet.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>',
    },
    'feesheet.required_intro': {
      label: 'Required Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Essential fees and utilities to help you understand your base costs and requirements.</p>',
    },
    'feesheet.optional_intro': {
      label: 'Optional Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Optional services, amenities, and rentable spaces to enhance your living experience. Typically charged per apartment but may be per person or pet. Contact the Leasing Team for further clarification.</p>',
    },
    'feesheet.situational_intro': {
      label: 'Situational Fees Intro',
      type: 'richtext',
      defaultValue: '<p>Sometimes life happens, and we understand things don&rsquo;t always go as planned. Situational fees may apply in certain circumstances and can include charges related to damage, excessive cleaning, or other incident-based services. These fees help us maintain a safe, clean, and comfortable community for all residents.</p>',
    },
    'feesheet.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue: '<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>',
    },
  },
  slotGroups: {
    'feesheet.application': applicationDef,
    'feesheet.admin': adminDef,
    'feesheet.reservation': reservationDef,
    'feesheet.security': securityDef,
    'feesheet.amenity': amenityDef,
    'feesheet.amenity_renewal': amenityRenewalDef,
    'feesheet.occupancy': occupancyDef,
    'feesheet.certificate': certificateDef,
    'feesheet.electric': electricDef,
    'feesheet.gas': gasDef,
    'feesheet.water': waterDef,
    'feesheet.utility_admin': utilityAdminDef,
    'feesheet.utility_setup': utilitySetupDef,
    'feesheet.trash': trashDef,
    'feesheet.pest': pestDef,
    'feesheet.custom_required': customRequiredDef,
    'feesheet.pet_fee': petFeeDef,
    'feesheet.pet_rent': petRentDef,
    'feesheet.pet_fee_2nd': petFee2ndDef,
    'feesheet.pet_rent_2nd': petRent2ndDef,
    'feesheet.parking_reserved': parkingReservedDef,
    'feesheet.parking_covered': parkingCoveredDef,
    'feesheet.parking_garage': parkingGarageDef,
    'feesheet.parking_unreserved': parkingUnreservedDef,
    'feesheet.parking_custom': parkingCustomDef,
    'feesheet.parking_custom_2': parkingCustom2Def,
    'feesheet.storage': storageDef,
    'feesheet.washer': washerDef,
    'feesheet.custom_optional': customOptionalDef,
    'feesheet.custom_optional_2': customOptional2Def,
    'feesheet.custom_optional_3': customOptional3Def,
    'feesheet.custom_optional_4': customOptional4Def,
    'feesheet.month_to_month': monthToMonthDef,
    'feesheet.transfer': transferDef,
    'feesheet.buyout': buyoutDef,
    'feesheet.late': lateDef,
    'feesheet.nsf': nsfDef,
    'feesheet.lockout': lockoutDef,
    'feesheet.custom_situational': customSituationalDef,
  },
};

(FeeSheet as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeSheet;
